/**
 * Globális színpaletta.
 *
 * A projekt vizuális egységét EZ adja: minden procedurálisan generált textúra
 * végül erre a 48 színre kvantálódik (rendezett Bayer-ditheringgel). Enélkül
 * a generátorok külön-külön szép, de együtt szedett-vedett képet adnának.
 *
 * A paletta öt családból áll, mindegyik egy sötét→világos rámpa:
 *   stone  — hideg szürke beton, alapfelület
 *   rust   — meleg barna/vörös oxidált fém
 *   tech   — kékeszöld panel, a „gépi" terek
 *   hazard — telített narancs/sárga, csak figyelmeztetésre és fényre
 *   flesh  — mély vörös/lila, a démoni terek
 *
 * Gazda: C
 */

export type Rgb = [number, number, number]

export function hexToRgb(hex: number): Rgb {
  return [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255]
}

export function rgbToHex(r: number, g: number, b: number): number {
  return (r << 16) | (g << 8) | b
}

/** Sötéttől világosig rendezett színrámpák. */
export const RAMPS = {
  stone: [0x111318, 0x1e2229, 0x2f353d, 0x454d57, 0x5e6772, 0x7b858f, 0x9aa4ae, 0xc2cad2],
  rust: [0x1a0f08, 0x30190d, 0x4c2915, 0x6b3d1e, 0x8c5528, 0xad7038, 0xc9904f, 0xe3b877],
  tech: [0x081318, 0x102630, 0x1a3d4a, 0x265866, 0x347684, 0x4596a6, 0x60bccb, 0x8ee2ee],
  hazard: [0x241708, 0x452a0d, 0x6b4213, 0x94601a, 0xbb8020, 0xdaa42a, 0xf0c645, 0xffe89a],
  flesh: [0x160709, 0x2a0d13, 0x45141e, 0x611c2a, 0x812739, 0xa4374c, 0xc44f64, 0xe07a8c],
} as const satisfies Record<string, readonly number[]>

export type RampName = keyof typeof RAMPS

/** A teljes kvantálási paletta: minden rámpa + néhány közös hangsúlyszín. */
export const PALETTE: number[] = (() => {
  const out: number[] = []
  for (const key of Object.keys(RAMPS) as RampName[]) out.push(...RAMPS[key])
  // Hangsúlyszínek: ezek soha nem fordulnak elő nagy felületen, csak
  // fényforrásokon, kijelzőkön, vérnyomokon — de kellenek a palettában,
  // különben a kvantálás tönkretenné őket.
  out.push(
    0x000000,
    0xffffff,
    0x00ff9c, // aktív terminál
    0xff3b1f, // vészjelzés / plazma-becsapódás
    0x8affff, // energia
    0x2a0004, // alvadt vér
    0x5c0a0f, // friss vér
    0xffd98a, // meleg lámpafény
  )
  return out
})()

const PALETTE_RGB: Rgb[] = PALETTE.map(hexToRgb)

/**
 * 4×4 Bayer-mátrix rendezett ditheringhez. A kvantálás enélkül sávosodna
 * a nagy, lassan változó felületeken (padló, fal) — a dithering ezt
 * finom, retro-hű pixelzajjá alakítja.
 */
const BAYER4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
]

/**
 * Egy RGBA pixelpuffert a globális palettára kvantál, Bayer-ditheringgel.
 * `strength` 0 = nincs dithering, 1 = teljes.
 */
export function quantizeToPalette(data: Uint8ClampedArray, width: number, strength = 1): void {
  const spread = 14 * strength
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const x = p % width
    const y = (p / width) | 0
    const bias = (BAYER4[y & 3][x & 3] / 16 - 0.5) * spread

    const r = data[i] + bias
    const g = data[i + 1] + bias
    const b = data[i + 2] + bias

    let best = 0
    let bestD = Infinity
    for (let c = 0; c < PALETTE_RGB.length; c++) {
      const pc = PALETTE_RGB[c]
      // Súlyozott euklideszi távolság — a zöldre érzékenyebb a szem.
      const dr = r - pc[0]
      const dg = g - pc[1]
      const db = b - pc[2]
      const d = dr * dr * 0.3 + dg * dg * 0.59 + db * db * 0.11
      if (d < bestD) {
        bestD = d
        best = c
      }
    }
    const pc = PALETTE_RGB[best]
    data[i] = pc[0]
    data[i + 1] = pc[1]
    data[i + 2] = pc[2]
  }
}

/** Rámpából mintavétel; `t` 0..1, lineáris interpolációval. */
export function sampleRamp(ramp: readonly number[], t: number): Rgb {
  const x = Math.max(0, Math.min(0.9999, t)) * (ramp.length - 1)
  const i = Math.floor(x)
  const f = x - i
  const a = hexToRgb(ramp[i])
  const b = hexToRgb(ramp[Math.min(ramp.length - 1, i + 1)])
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f]
}
